import { ReactNode, RefObject, useEffect, useRef } from "react";
import cx from "classnames";
import debounce from "lodash/debounce";
import max from "lodash/max";
import values from "lodash/values";

import Separator from "../Separator";

import styles from "./FormRow.scss";

interface FormRowProps {
  children?: ReactNode;
  isWithoutPadding?: boolean;
  separator?: boolean;
  spacing?: "md" | "lg";
  className?: string;
  separatorPosition?: "top" | "bottom";
  paddingPosition?: "top" | "bottom";
}

const setHeadersHeight = (rowRef: RefObject<HTMLDivElement>) => {
  const rowEl = rowRef && rowRef.current;

  if (rowEl) {
    const headerElements = values(rowEl.querySelectorAll("[data-header]")) as HTMLElement[];
    const heights = Array.isArray(headerElements) ? headerElements.map(el => el.offsetHeight) : [0];
    const highest = max(heights);

    headerElements.forEach(el => {
      const elCopy = el;

      if (elCopy && elCopy.style) {
        elCopy.style.minHeight = `${highest}px`;
      }
    });
  }
};

const useSetupHeadersHeightCalculations = (rowRef: RefObject<HTMLDivElement>) => {
  const handleWindowResize = debounce(() => setHeadersHeight(rowRef), 300);

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [handleWindowResize]);

  return null;
};

const FormRow = ({
  children,
  isWithoutPadding = false,
  separator = false,
  spacing = "md",
  className,
  separatorPosition = "bottom",
  paddingPosition = "bottom"
}: FormRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);

  useSetupHeadersHeightCalculations(rowRef);

  return (
    <>
      {separator && separatorPosition === "top" && (
        <Separator separatorPosition={separatorPosition} />
      )}
      <div
        className={cx(
          {
            [styles.formRowContainer]: true,
            [styles.hasSeparator]: separator,
            [styles.withoutPadding]: isWithoutPadding,
            [styles.paddingTop]: paddingPosition === "top",
            [styles[`spacing-${spacing}`]]: true
          },
          className
        )}
        data-testid="formrow"
        ref={rowRef}
      >
        {children}
      </div>
      {separator && separatorPosition === "bottom" && (
        <Separator separatorPosition={separatorPosition} />
      )}
    </>
  );
};

export default FormRow;
